import React, { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import Dialog from '@material-ui/core/Dialog';
import { alertModal } from '@apply/ui';

export interface SaveTemplateFormProps {
    title?: string;
    templateName?: string;
    templateId: number;
    existingTemplates:
        | {
              display: string;
              value: string;
          }[]
        | null
        | undefined;
    onConfirm?: (name: string, isNew: boolean) => void;
    onCancel?: () => void;
    okLabel?: string;
    cancelLabel?: string;
}

const styles = {
    modal: {
        width: 600,
    },
    content: {
        padding: '20px',
    },
    fields: {
        padding: '10px',
    },
};

export const SaveTemplateForm: React.FC<SaveTemplateFormProps> = ({
    title = 'Save Template',
    templateName,
    templateId = 0,
    existingTemplates = [],
    okLabel = 'Save',
    cancelLabel = 'Cancel',
    onCancel,
    onConfirm,
}) => {
    const [name, set_name] = useState<string>(templateName);
    const [isNew, set_isNew] = useState<boolean>(templateId === 0);

    const handleConfirm = useCallback(() => {
        const duplicateNameForm = existingTemplates?.find(
            (f) => f.display === name && parseInt(f.value) !== (isNew ? 0 : templateId)
        );
        if (duplicateNameForm) {
            alertModal({
                message: `The name "${duplicateNameForm.display}" already exists. Please provide an alternate name.`,
            });
            return;
        }
        onConfirm && onConfirm(name, isNew);
        removeSaveTemplateModal();
    }, [onConfirm, name, isNew]);

    const handleCancel = useCallback(() => {
        onCancel && onCancel();
        removeSaveTemplateModal();
    }, [onCancel]);

    return (
        <Dialog
            maxWidth='lg'
            open={true}
            onClose={handleCancel}
            aria-labelledby='form-dialog-title'
        >
            <div className='modal-container'>
                <div className='modal-backdrop'></div>
                <div
                    className='modal'
                    tabIndex={-1}
                    role='dialog'
                    style={styles.modal}
                >
                    <div
                        className='page-header'
                        role='banner'
                    >
                        <h1>{title}</h1>
                        <button
                            type='button'
                            className='close'
                            data-dismiss='modal'
                            aria-label='Close'
                            onClick={handleCancel}
                        >
                            <span aria-hidden='true'>&times;</span>
                        </button>
                    </div>
                    <div
                        className='modal-dialog'
                        role='document'
                        style={styles.content}
                    >
                        <div style={styles.fields}>
                            <input
                                type='radio'
                                name='saveType'
                                value='0'
                                checked={!isNew}
                                onChange={(e) => set_isNew(e.target.value === '0')}
                                disabled={templateId === 0}
                            />
                            &nbsp; Existing Template &nbsp;&nbsp;&nbsp;
                            <input
                                type='radio'
                                name='saveType'
                                value='1'
                                checked={isNew}
                                onChange={(e) => set_isNew(e.target.value === '1')}
                            />
                            &nbsp; New Template
                        </div>

                        <div style={styles.fields}>
                            Template Name:{' '}
                            <input
                                type='text'
                                className='forms-input-field'
                                name='templateName'
                                value={name}
                                onChange={(e) => set_name(e.target.value)}
                                maxLength={200}
                            />
                        </div>
                    </div>
                    <div className='button-group'>
                        <button
                            onClick={handleConfirm}
                            className='button'
                            aria-label='OK'
                            disabled={!name}
                        >
                            {okLabel}
                        </button>
                        <button
                            onClick={handleCancel}
                            className='button'
                            aria-label='Cancel'
                        >
                            {cancelLabel}
                        </button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

const mountPointId = 'bfi-apply-savetemplateform-modal';

// the ignore below are because really the branch being ignored is providing
// arguably over-zealous protection against throwing null-ref exceptions when something
// in the browser has just gone horribly wrong, but really can't be tested
const removeSaveTemplateModal = (): void => {
    const mountPoint = document.getElementById(mountPointId);
    /* istanbul ignore else */
    if (mountPoint) {
        ReactDOM.unmountComponentAtNode(mountPoint);
        mountPoint.parentNode.removeChild(mountPoint);
    }
};

export const saveTemplateForm = (props: SaveTemplateFormProps): void => {
    let mountPoint = document.getElementById(mountPointId);
    if (mountPoint) {
        ReactDOM.render(<SaveTemplateForm {...props} />, mountPoint);
    } else {
        mountPoint = document.createElement('div');
        mountPoint.id = mountPointId;
        document.body.appendChild(mountPoint);
        ReactDOM.render(<SaveTemplateForm {...props} />, mountPoint);
    }
};
